import { OrderConfigItem } from './strapi/order'
import { Step } from './strapi/steps'
import { ConditionDictionary, RelationCondition } from './strapi/conditions'
import {
  Deal,
  AttributeConfig,
  OrderScenario,
  PackageConfig,
  PricesRegion,
  Requirements,
  StrapiID,
} from './strapi'
import { RegionCode } from '../regions'
import { CurrencyCode } from '../currency'
import { DiscountType, RefractionAction } from '../schema'
import { Question } from './question'
import { Recommendations } from '.'

export type StepMode = 'bundle' | 'frame'

export type StepModeConfig = {
  mode: StepMode
  initialStep: StrapiID | null
}

export type FlowConfig = {
  id: StrapiID
  desc?: string
  name: string
  stepModes: StepModeConfig[]
  nextOrder?: 'any' | 'same-deal'
  isSelfService?: boolean
}

export type CustomOption = {
  id: string | number
  name: string
  price: PricesRegion | null
  nextStep: string | number | null
  requires: Requirements
  condition: string | null
  action?: RefractionAction | null
}

type VariableSettings = {
  summary: boolean
  incremental: boolean
  name: string
}

export type DiscountConfig = {
  other: DiscountType[]
  max: number
  scenarios?: OrderScenario[]
}

/**
 * @deprecated Please use the new qty options
 */
type QtyRequire = 'require'

export type StockConfig = {
  type: 'global' | 'local'
  qty: 'ignore' | 'store' | 'remote' | 'total' | QtyRequire
  plc: string[] | null
  requirements: Requirements
}

export type VatConfig = 'required' | 'optional' | 'hidden'

export type RegionPolicyConfig = {
  policy_key: string
  required: boolean
  policy_revision: string
}

type RegionConditionKey = 'vf' | 'fh' | 'fhVf' | 'vfUpsell' | 'office'

type RegionConditions = Partial<Record<RegionConditionKey, RelationCondition>>

export type RegionConfig = {
  id: StrapiID
  erpCode: string
  countryCode: RegionCode
  currency: CurrencyCode
  vfCondition?: RelationCondition
  fhCondition?: RelationCondition
  fhVfCondition?: RelationCondition
  vfUpsellCondition?: RelationCondition
  vat?: VatConfig
  stock?: StockConfig[]
  coupon?: DiscountConfig
  credit?: DiscountConfig
  referral?: DiscountConfig
  'gift-card'?: DiscountConfig
  promotion?: DiscountConfig
  prescriptionRoles?: string[]
  priceCode?: RegionCode
  meliboChatKey?: string
  conditions?: RegionConditions
  policies?: RegionPolicyConfig[]
}

export type ColorDef = {
  color: string
  opacity: number
  opaqueColor?: string
}

export type ColorConfig = {
  id: StrapiID
  name: string
  code: string
  front: ColorDef[]
  back: ColorDef[]
}

export const opacityToDecimal = (opacity: number) => {
  const dec = opacity / 100
  return Math.round(dec * 100) / 100
}

export type Tag = {
  name: string
  navigation: boolean
}

export type ConfigCondition = {
  name: string
  requires: Requirements
  condition: string | null
  value: unknown
}

export type RemoteConfig = {
  // Url to use as image base
  baseImageUrl?: string
  // videos: Record<string, Record<string, VideoConfig>>
  deals: Record<string, Deal[]>
  steps: Record<string, Step>
  items: Record<string, OrderConfigItem> // Maybe we should key this by variable?
  conditions: ConditionDictionary
  region: RegionConfig
  customOptions: Record<string, CustomOption>
  variables?: Record<string, VariableSettings>
  packages: Record<string, PackageConfig>
  colors: Record<string, ColorConfig>
  /**
   * Optional as older versions of the app may not have this
   */
  tags?: Record<string, Tag>
  attributes?: Record<string, AttributeConfig>
  configConditions?: Record<string, ConfigCondition>
  questions?: Question[]
  /**
   * Recommendations by variable
   */
  recommendations: Record<string, Recommendations.Item[]> | undefined
  flows?: Record<string, FlowConfig>
}

export type IntroStyle = 'blur' | 'light' | 'dark'
